'use client'

import { useState, useRef, useCallback } from 'react'
import { Globe2, MessageSquare, Repeat2, Send, ThumbsUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'

type Asset = {
  type: string
  url: string | null
  thumbnailUrl: string | null
  title: string | null
}

type LinkedInPostCardProps = {
  imageUrl: string | null
  caption: string
  hashtags: string[]
  username: string
  assets?: Asset[]
}

function Grid({ assets }: { assets: Asset[] }) {
  const count = assets.length
  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        {assets.map((asset, i) => {
          const displayUrl =
            asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
          return (
            <div key={i} className="relative aspect-square bg-input/40">
              {asset.type === 'VIDEO' ? (
                <video
                  src={asset.url ?? undefined}
                  controls
                  playsInline
                  className="size-full object-cover"
                />
              ) : (
                <img
                  src={displayUrl ?? undefined}
                  alt=""
                  className="size-full object-cover"
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }

  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        <div className="relative aspect-[4/3] bg-input/40 col-span-2">
          {assets[0].type === 'VIDEO' ? (
            <video
              src={assets[0].url ?? undefined}
              controls
              playsInline
              className="size-full object-cover"
            />
          ) : (
            <img
              src={assets[0].url ?? undefined}
              alt=""
              className="size-full object-cover"
            />
          )}
        </div>
        <div className="relative aspect-square bg-input/40">
          {assets[1].type === 'VIDEO' ? (
            <video
              src={assets[1].url ?? undefined}
              controls
              playsInline
              className="size-full object-cover"
            />
          ) : (
            <img
              src={assets[1].url ?? undefined}
              alt=""
              className="size-full object-cover"
            />
          )}
        </div>
        <div className="relative aspect-square bg-input/40">
          {assets[2].type === 'VIDEO' ? (
            <video
              src={assets[2].url ?? undefined}
              controls
              playsInline
              className="size-full object-cover"
            />
          ) : (
            <img
              src={assets[2].url ?? undefined}
              alt=""
              className="size-full object-cover"
            />
          )}
        </div>
      </div>
    )
  }

  if (count === 4) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        {assets.map((asset, i) => {
          const displayUrl =
            asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
          return (
            <div key={i} className="relative aspect-square bg-input/40">
              {asset.type === 'VIDEO' ? (
                <video
                  src={asset.url ?? undefined}
                  controls
                  playsInline
                  className="size-full object-cover"
                />
              ) : (
                <img
                  src={displayUrl ?? undefined}
                  alt=""
                  className="size-full object-cover"
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-0.5">
      {assets.slice(0, 4).map((asset, i) => {
        const displayUrl =
          asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
        return (
          <div key={i} className="relative aspect-square bg-input/40">
            {asset.type === 'VIDEO' ? (
              <video
                src={asset.url ?? undefined}
                controls
                playsInline
                className="size-full object-cover"
              />
            ) : (
              <img
                src={displayUrl ?? undefined}
                alt=""
                className="size-full object-cover"
              />
            )}
            {count > 4 && i === 3 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-b">
                <span className="text-xl font-bold text-white">
                  +{count - 4}
                </span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Carousel({
  assets,
  caption,
}: {
  assets: Asset[]
  caption: string
}) {
  const [slide, setSlide] = useState(0)
  const total = assets.length
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  const goNext = useCallback(() => {
    setSlide((s) => Math.min(s + 1, total - 1))
  }, [total])

  const goPrev = useCallback(() => {
    setSlide((s) => Math.max(s - 1, 0))
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true)
    setStartX(e.clientX)
    setDragOffset(0)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return
    setDragOffset(e.clientX - startX)
  }

  const handlePointerUp = () => {
    if (!isDragging) return
    setIsDragging(false)
    if (dragOffset < -50 && slide < total - 1) {
      goNext()
    } else if (dragOffset > 50 && slide > 0) {
      goPrev()
    }
    setDragOffset(0)
  }

  if (total === 1) {
    const asset = assets[0]
    const displayUrl =
      asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
    return (
      <div className="relative w-full bg-input/40">
        <img
          src={displayUrl ?? undefined}
          alt={caption.slice(0, 80)}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    )
  }

  const translateX = -(slide * 100) + dragOffset

  return (
    <div className="relative w-full bg-input/40 overflow-hidden">
      <div
        className="flex select-none"
        style={{ transform: `translateX(${translateX}%)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {assets.map((asset) => {
          const displayUrl =
            asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
          return (
            <div
              key={asset.url ?? asset.title ?? ''}
              className="w-full shrink-0"
            >
              <img
                src={displayUrl ?? undefined}
                alt={caption.slice(0, 80)}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )
        })}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={slide === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Previous asset"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={slide >= total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Next asset"
          >
            <ChevronRight className="size-4" />
          </button>
        </>
      )}

      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
          {assets.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSlide(i)}
              className={`size-1.5 rounded-full transition-all ${
                i === slide ? 'bg-white w-4' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function LinkedInPostCard({
  imageUrl,
  caption,
  hashtags,
  username,
  assets,
}: LinkedInPostCardProps) {
  const t = useTranslations('LinkedInPostCard')

  const carouselAssets =
    assets && assets.length > 0
      ? assets.filter((a) => a.url || a.thumbnailUrl)
      : []

  const displayAssets =
    carouselAssets.length > 0 ? carouselAssets : [{ type: 'IMAGE', url: imageUrl, thumbnailUrl: null, title: null }]

  return (
    <div className="w-full mx-auto rounded-2xl bg-card shadow-sm overflow-hidden">
      <div className="flex items-start justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-3">
          <div
            className="size-12 shrink-0 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
            }}
            aria-hidden
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold leading-none">
              {username}
            </span>
            <span className="text-xs text-muted-foreground mt-0.5">
              {t('jobTitlePlaceholder')}
            </span>
            <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
              <span>2h</span>
              <span>•</span>
              <Globe2 className="size-3" />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pb-3">
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{caption}</p>
      </div>

      {hashtags.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-sm text-blue-600 dark:text-blue-400">
            {hashtags.map((tag, i) => (
              <span key={i}>
                {i > 0 ? ' ' : ''}
                {tag}
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Media / Carousel or Grid */}
      {displayAssets.length > 1 ? (
        <Grid assets={displayAssets} />
      ) : (
        <Carousel assets={displayAssets} caption={caption} />
      )}

      <div className="flex items-center gap-1 px-2 py-2 border-t border-border/50">
        {[
          { icon: ThumbsUp, label: t('likeLabel') },
          { icon: MessageSquare, label: t('commentLabel') },
          { icon: Repeat2, label: t('repostLabel') },
          { icon: Send, label: t('sendLabel') },
        ].map(({ icon: Icon, label }) => (
          <button
            key={label}
            type="button"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-xs font-medium"
          >
            <Icon className="size-4 shrink-0" />
            <span className="hidden md:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="px-4 pb-4 pt-2">
        <Button
          type="button"
          className="w-full"
          onClick={() => toast.info(t('comingSoon'))}
        >
          {t('publish')}
        </Button>
      </div>
    </div>
  )
}
